import {CalculationRowType, IncomeTypesLogic, EnumerationsLogic, CalcRowsLogic, Translations} from "@salaxy/core";
import { EditDialogKnownActions, UiHelpers, RowTypeService } from "../../services";
import { InputController } from "./InputController";

/**
 * Controller behind form control which edits/displays row types for pay stats calculations.
 */

export class InputPayStatsRowTypeController extends InputController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["UiHelpers", "RowTypeService"];


  constructor(private uiHelpers: UiHelpers, private rowTypeService: RowTypeService) {
    super();
  }

  /** Set the values and defaults on init */
  public $onInit() {
    super.$onInit();
  }

  /** Returns true if this is an exclusive row type.  */
  public get isExclusive(): boolean {
    return (this.value ?? "").startsWith("!");
  }

  /** Returns text for the row type */
  public get text(): string {
    // Possible types
    /*
    incomeType.[code]
    incomeType.[code]-[unit]

    rowType.[rowType]
    rowType.[rowType].[kind]

    rowType.[rowType]-[unit]
    rowType.[rowType].[kind]-[unit]

    type.[type]
    type.[type]/[kind]

    type.[type]-[unit]
    type.[type]/[kind]-[unit]

    */

    if (!this.value) {
      return "";
    }

    const types = this.parseRowType(this.value);
    switch(types.baseType) {
      case "rowType":
        return Translations.get("SALAXY.NG1.WorkerHolidaysComponent.UI.inputs.rowType") + ": " + this.getCalculationRowTypeLabel(types.type as CalculationRowType, types.kind) + (types.unit ? ` (${EnumerationsLogic.getEnumLabel("CalculationRowUnit", types.unit, null, null)})` : "");
      case "incomeType":
        {
          const incomeType = IncomeTypesLogic.getAll(null).find( (x) => (x.code + "") == types.type);
          return "Tulolaji: " + `${incomeType?.code} - ` + incomeType?.label + (types.unit ? ` (${EnumerationsLogic.getEnumLabel("CalculationRowUnit", types.unit, null, null)})` : "");
        }
      case "type":
        return Translations.get("SALAXY.NG1.WorkerHolidaysComponent.UI.inputs.wageType") + ": " + types.type + (types.kind ? `/${types.kind}`:"") + (types.unit ? ` (${EnumerationsLogic.getEnumLabel("CalculationRowUnit", types.unit, null, null)})` : "");
    }
  }

  /** Edit entry. */
  public edit() {
    const rowTypeService = this.rowTypeService;
    const logic = {
      readOnly: this.getReadOnly(),
      _userDefinedRow:{
        item: null,
        row: {
          data: {
            get kind() {
              return logic._userDefinedRow.item.kind;
            },
            set kind(value) {
              logic._userDefinedRow.item.kind = value;
            },
          },
          get rowType() {
            if (logic._userDefinedRow.item.baseType == "type") {
              if (!logic._userDefinedRow.item._type && logic._userDefinedRow.item.type) {
               logic._userDefinedRow.item._type = rowTypeService.getConfigs().find(x => x.type == logic._userDefinedRow.item.type)?.rowType;
              }
              return logic._userDefinedRow.item._type;
            }
            else
            {
              return logic._userDefinedRow.item.type;
            }
          },
          set rowType(value) {
            if (logic._userDefinedRow.item.baseType == "type") {
              logic._userDefinedRow.item._type = value;
            }
            else
            {
              logic._userDefinedRow.item.type = value;
            }
          },
          get type() {
            if (logic._userDefinedRow.item.baseType == "type") {
              return logic._userDefinedRow.item.type;
            }
            else
            {
              return logic._userDefinedRow.item._type;
            }
          },
          set type(value) {
            if (logic._userDefinedRow.item.baseType == "type") {
              logic._userDefinedRow.item.type = value;
            }
            else
            {
              logic._userDefinedRow.item._type = value;
            }
          }
        }
      },
      asUserDefinedRow: (item ) => {
        logic._userDefinedRow.item = item;
        return logic._userDefinedRow.row;
      }
    };
    this.uiHelpers.openEditDialog(
      "salaxy-components/modals/form-controls/PayStatsRowTypeEdit.html",
      this.parseRowType(this.value),
      logic
      ).then((result) => {
        if (result.action === EditDialogKnownActions.Ok) {
          this.value = result.item.baseType == "type" ? this.composeType(result.item): this.composeRowType(result.item);
          this.onChange();
        }
      });
  }

  private getCalculationRowTypeLabel(rowType: CalculationRowType, kind: string): string {
    let label = "";
    if (rowType === CalculationRowType.IrIncomeType) {
      label = IncomeTypesLogic.getAll(null).find( (x) => (kind && x.transactionCode === kind))?.label;
    }
    else if (kind) {
      const rowConfig = CalcRowsLogic.getRowConfig(rowType);
      label =  EnumerationsLogic.getEnumLabel(rowConfig.kind.name, kind, null, null);
    }
    return label || EnumerationsLogic.getEnumLabel("CalculationRowType", rowType, null, null);

  }

  private parseRowType(value: string): {baseType?: string, type?: string, kind?: string, unit?: string, isExclusive?: boolean} {
    // Possible types
    /*
    incomeType.[code]
    incomeType.[code]-[unit]

    rowType.[rowType]
    rowType.[rowType].[kind]

    rowType.[rowType]-[unit]
    rowType.[rowType].[kind]-[unit]

    type.[type]
    type.[type]/[kind]

    type.[type]-[unit]
    type.[type]/[kind]-[unit]

    */
    if (!value) {
      return { isExclusive: false};
    }

    let isExclusive = false;
    let unit = "";
    let kind = "";
    let type = "";
    if (value.startsWith("!")) {
      isExclusive = true;
      value = value.substring(1);
    }

    const baseType = value.split(".")[0];

    if (value.lastIndexOf("-") > 0) {
      unit = value.substring(value.lastIndexOf("-") + 1);
      value = value.substring(0, value.lastIndexOf("-"));
    }

    switch(baseType) {
      case "incomeType":
        type = value.split(".")[1];
        // ok
        break;
      case "rowType":
        {
          const dotParts = value.split(".");
          type = dotParts[1];
          if (dotParts.length == 3) {
            kind = dotParts[2];
          }
        }
        break;
      case "type":
        {
          const fullType = value.substring(value.indexOf(".") + 1);
          const nameParts = fullType.split("/");
          if (nameParts.length >= 3) {
            type = "/" + nameParts[1] + "/" + nameParts[2];
          }
          if (nameParts.length == 4) {
            kind = nameParts[3];
          }
        }
        break;
    }

    return {baseType, type, kind, unit, isExclusive};
  }

  private composeRowType(types: {baseType?: string, type?: string, kind?: string, unit?: string, isExclusive?: boolean}): string {
    if (!types?.baseType || !types?.type) {
      return "";
    }
    let rowType = `${types.baseType}.${types.type}`;
    if (types.kind) {
      rowType += `.${types.kind}`;
    }
    if (types.unit && types.unit !== "undefined") {
      rowType += `-${types.unit}`;
    }
    if (types.isExclusive) {
      rowType = "!" + rowType;
    }
    return rowType;
  }

  private composeType(types: {baseType?: string, type?: string, kind?: string, unit?: string, isExclusive?: boolean}): string {
    if (!types?.baseType || !types?.type) {
      return "";
    }
    let wageType = `${types.baseType}.${types.type}`;
    if (types.kind) {
      wageType += `/${types.kind}`;
    }
    if (types.unit && types.unit !== "undefined") {
      wageType += `-${types.unit}`;
    }
    if (types.isExclusive) {
      wageType = "!" + wageType;
    }
    return wageType;
  }
}
